'use client';
/*
 * Documentation:
 * Icon with background — https://app.subframe.com/6fc7f5fbb098/library?component=Icon+with+background_c5d68c0e-4c0c-4cff-8d8c-6ff334859b3a
 */

import * as SubframeCore from '@subframe/core';
import { FeatherCheck } from '@subframe/core';
import React from 'react';
import * as SubframeUtils from '../utils';

const extractIconColorClasses = (className?: string): string | undefined => {
  if (!className) return undefined;

  const colorClasses = className
    .split(/\s+/)
    .filter(
      (token) =>
        token.startsWith('text-') || token.startsWith('fill-') || token.startsWith('stroke-'),
    );

  if (colorClasses.length === 0) return undefined;
  return colorClasses.map((token) => (token.startsWith('!') ? token : `!${token}`)).join(' ');
};

interface IconWithBackgroundRootProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'brand' | 'neutral' | 'error' | 'success' | 'warning';
  size?: 'x-large' | 'large' | 'medium' | 'small' | 'x-small';
  icon?: React.ReactNode;
  square?: boolean;
  className?: string;
}

const IconWithBackgroundRoot = React.forwardRef<HTMLDivElement, IconWithBackgroundRootProps>(
  function IconWithBackgroundRoot(
    {
      variant = 'brand',
      size = 'x-small',
      icon = <FeatherCheck />,
      square = false,
      className,
      ...otherProps
    }: IconWithBackgroundRootProps,
    ref,
  ) {
    const iconColorClassName = extractIconColorClasses(className);

    return (
      <div
        className={SubframeUtils.twClassNames(
          'group/c5d68c0e bg-brand-100 flex h-5 w-5 items-center justify-center gap-2 rounded-full transition-colors',
          {
            'rounded-md': square,
            'h-6 w-6': size === 'small',
            'h-8 w-8': size === 'medium',
            'h-12 w-12': size === 'large',
            'h-16 w-16': size === 'x-large',
            'bg-warning-100': variant === 'warning',
            'bg-success-100': variant === 'success',
            'bg-error-100': variant === 'error',
            'bg-neutral-100': variant === 'neutral',
          },
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        {icon ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "subframe-icon-wrapper font-['Inter'] text-[10px] leading-[12px] font-[400] transition-colors",
              {
                'text-caption font-caption': size === 'small',
                'text-body font-body': size === 'medium',
                'text-heading-2 font-heading-2': size === 'large',
                'text-heading-1 font-heading-1': size === 'x-large',
              },
              iconColorClassName ??
                (className?.includes('text-white')
                  ? '!text-white'
                  : variant === 'warning'
                    ? '!text-warning-800'
                    : variant === 'success'
                      ? '!text-success-800'
                      : variant === 'error'
                        ? '!text-error-800'
                        : variant === 'neutral'
                          ? '!text-neutral-700'
                          : '!text-brand-600'),
            )}
          >
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
      </div>
    );
  },
);

export const IconWithBackground = IconWithBackgroundRoot;
